<template>
  <div>
    <div v-if="loading" class="ma-6 d-flex align-center justify-center">
      <v-progress-circular indeterminate color="primary" class="mr-2" size="20" />
      {{ $translate('Loading') }}
    </div>
    <div v-else-if="!timeRows.length" class="ma-6 d-flex align-center justify-center">
      <v-icon class="mr-2">mdi-calendar-alert-outline</v-icon>{{ $translate('No available times found') }}
    </div>
    <div v-else>
      <div class="week-grid-scroll">
        <table class="week-grid" :style="{ minWidth: minGridWidth }">
          <thead>
            <tr>
              <th class="week-grid__label-col"></th>
              <th v-for="day in days" :key="day.date" class="text-center">
                <div class="font-weight-bold">{{ day.weekday }}</div>
                <div class="text-caption">{{ day.label }}</div>
                <v-btn
                  small text class="text-none mt-1 px-1"
                  :disabled="disabled || (!dayHasBookable(day.date) && !selectedDays.includes(day.date))"
                  @click="$emit('toggle-day', day.date)"
                >
                  {{ selectedDays.includes(day.date) ? $translate('Remove all times') : $translate('Select all times') }}
                </v-btn>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td :colspan="days.length + 1" class="pa-0"><v-divider /></td>
            </tr>
            <tr v-for="row in timeRows" :key="row.time">
              <td class="font-weight-bold text-no-wrap week-grid__time-label">{{ row.label }}</td>
              <td v-for="day in days" :key="day.date + row.time">
                <button
                  type="button"
                  class="week-cell"
                  :class="'week-cell--' + stateFor(day.date, row.time)"
                  :disabled="disabled || stateFor(day.date, row.time) === 'none'"
                  @click="onCell(day.date, row.time)"
                >
                  {{ cellText(day.date, row.time) }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex align-center flex-wrap mt-4" style="gap:20px;">
        <span class="d-flex align-center"><span class="legend-swatch week-cell--bookable mr-2"></span>{{ $translate('Available') }}</span>
        <span class="d-flex align-center"><span class="legend-swatch week-cell--selected mr-2"></span>{{ $translate('Selected') }}</span>
        <span class="d-flex align-center"><span class="legend-swatch week-cell--none mr-2"></span>{{ $translate('Unavailable') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WeekOverviewGrid',
  props: {
    days: { type: Array, required: true },          // [{date, weekday, label}]
    timeRows: { type: Array, default: () => [] },     // [{time:'09:00', label:'09–09:55'}, ...]
    cellStates: { type: Object, default: () => ({}) }, // 'YYYY-MM-DD|HH:MM' -> 'bookable'|'selected'|'none'
    selectedDays: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    minGridWidth() {
      return `${116 + this.days.length * 100}px`;
    },
  },
  methods: {
    stateFor(date, time) {
      return this.cellStates[`${date}|${time}`] || 'none';
    },
    dayHasBookable(date) {
      return this.timeRows.some(r => this.stateFor(date, r.time) === 'bookable');
    },
    cellText(date, time) {
      const s = this.stateFor(date, time);
      if (s === 'bookable') return this.$translate('Available');
      if (s === 'selected') return this.$translate('Selected');
      return '';
    },
    onCell(date, time) {
      if (this.stateFor(date, time) === 'none') return;
      this.$emit('book-slot', { date, time });
    },
  },
};
</script>

<style scoped>
.week-grid-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.week-grid {
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 10px 14px;
}
.week-grid__label-col {
  width: 116px;
}
.week-grid__time-label {
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
}
.week-cell {
  display: block;
  width: 100%;
  border: none;
  appearance: none;
  font-family: inherit;
  border-radius: 8px;
  padding: 12px 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  user-select: none;
  min-height: 20px;
  transition: filter 0.15s ease;
}
.week-cell:hover:enabled {
  filter: brightness(0.97);
}
.week-cell:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
.week-cell:disabled {
  cursor: default;
}
.week-cell--bookable {
  background-color: #e4efdd;
  color: #5c8a4a;
}
.week-cell--selected {
  background-color: #b9d8a6;
  color: #2f5a24;
}
.week-cell--none {
  background-color: #f1f3f4;
  color: #9aa0a6;
  cursor: default;
}
.legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}
</style>
